import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Package, Loader2, Star } from "lucide-react";
import { toast } from "sonner";
import * as LucideIcons from "lucide-react";
import { Button } from "@/components/ui/button";

type Extension = {
  id: string;
  title: string;
  slug: string;
  short_description: string;
  icon_name: string;
  pricing_type: string;
  price_cents: number;
  is_active: boolean;
  is_suspended: boolean;
  health_areas: { name: string }[];
};

export default function Aplicativos() {
  const [extensions, setExtensions] = useState<Extension[]>([]);
  const [loading, setLoading] = useState(true);
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(new Set());
  const navigate = useNavigate();

  useEffect(() => {
    loadExtensions();
    loadFavorites();
  }, []);

  const loadExtensions = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get user's health area
      const { data: profile } = await supabase
        .from("profiles")
        .select("health_area_id, health_area")
        .eq("id", user.id)
        .single();

      let healthAreaId = profile?.health_area_id;

      // Fallback para dados antigos (profiles com health_area texto mas sem health_area_id)
      if (!healthAreaId && profile?.health_area) {
        const normalizeArea = (area: string) =>
          area.toLowerCase()
            .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
            .replace(/[^a-z]/g, '');

        const { data: allAreas } = await supabase
          .from("health_areas")
          .select("id, name")
          .eq("is_active", true);

        const normalizedUserArea = normalizeArea(profile.health_area);
        const matched = allAreas?.find(ha =>
          normalizeArea(ha.name) === normalizedUserArea
        );
        healthAreaId = matched?.id;
      }

      if (!healthAreaId) {
        toast.error("Área de saúde não encontrada no perfil");
        return;
      }

      // Get extensions for user's health area
      const { data: extensionAreas } = await supabase
        .from("extension_health_areas")
        .select(`
          extension_id,
          extensions (
            id,
            title,
            slug,
            short_description,
            icon_name,
            pricing_type,
            price_cents,
            is_active,
            is_suspended
          )
        `)
        .eq("health_area_id", healthAreaId);

      if (!extensionAreas) {
        setExtensions([]);
        return;
      }

      // Get health areas for each extension
      const extensionsWithAreas = await Promise.all(
        extensionAreas.map(async (item: any) => {
          const ext = item.extensions;

          const { data: areas } = await supabase
            .from("extension_health_areas")
            .select(`
              health_areas (name)
            `)
            .eq("extension_id", ext.id);

          return {
            ...ext,
            health_areas: areas?.map((a: any) => a.health_areas) || [],
          };
        })
      );

      setExtensions(extensionsWithAreas.filter((e) => e.is_active && !e.is_suspended));
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Erro desconhecido";
      toast.error("Erro ao carregar aplicativos: " + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (cents: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(cents / 100);
  };

  const loadFavorites = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: favorites } = await supabase
        .from("user_favorite_extensions")
        .select("extension_id")
        .eq("user_id", user.id);

      if (favorites) {
        setFavoriteIds(new Set(favorites.map((f) => f.extension_id)));
      }
    } catch (error) {
      console.error("Erro ao carregar favoritos:", error);
    }
  };

  const toggleFavorite = async (extensionId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevenir navegação ao clicar no botão

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Você precisa estar autenticado para favoritar aplicativos");
        return;
      }

      const isFavorite = favoriteIds.has(extensionId);

      if (isFavorite) {
        // Remover dos favoritos
        const { error } = await supabase
          .from("user_favorite_extensions")
          .delete()
          .eq("user_id", user.id)
          .eq("extension_id", extensionId);

        if (error) {
          console.error("Erro ao remover favorito:", error);
          throw new Error(error.message || "Erro ao remover favorito");
        }

        setFavoriteIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(extensionId);
          return newSet;
        });

        toast.success("Aplicativo removido dos favoritos");
      } else {
        // Adicionar aos favoritos
        const { error } = await supabase
          .from("user_favorite_extensions")
          .insert({
            user_id: user.id,
            extension_id: extensionId,
          });

        if (error) {
          console.error("Erro ao adicionar favorito:", error);
          throw new Error(error.message || "Erro ao adicionar favorito");
        }

        setFavoriteIds((prev) => new Set(prev).add(extensionId));
        toast.success("Aplicativo adicionado aos favoritos");
      }
    } catch (error: any) {
      console.error("Erro ao favoritar aplicativo:", error);
      const errorMessage = error?.message || error?.error?.message || "Erro desconhecido ao favoritar aplicativo";
      const fullError = JSON.stringify(error, null, 2);
      
      // Verificar se é erro de tabela não encontrada
      const isTableNotFound = 
        errorMessage.toLowerCase().includes("does not exist") ||
        errorMessage.toLowerCase().includes("relation") ||
        errorMessage.toLowerCase().includes("table") ||
        errorMessage.toLowerCase().includes("user_favorite_extensions") ||
        (error?.code === "42P01"); // PostgreSQL error code for "relation does not exist"
      
      if (isTableNotFound) {
        toast.error("Tabela de favoritos não encontrada", {
          description: "Execute a migration do banco de dados. Veja MIGRATION_INSTRUCTIONS.md para instruções.",
          duration: 8000,
        });
        console.error("Detalhes do erro:", fullError);
      } else {
        toast.error(`Erro ao favoritar aplicativo: ${errorMessage}`, {
          duration: 5000,
        });
        console.error("Detalhes completos do erro:", fullError);
      }
    }
  };

  const getIcon = (iconName: string) => {
    const Icon = (LucideIcons as any)[iconName] || Package;
    return Icon;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (extensions.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Aplicativos</h1>
          <p className="text-muted-foreground">
            Nenhum aplicativo disponível para sua área de saúde no momento
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Aplicativos</h1>
        <p className="text-muted-foreground">
          Descubra e utilize aplicativos para sua prática profissional
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {extensions.map((ext) => {
          const Icon = getIcon(ext.icon_name);

          return (
            <Card
              key={ext.id}
              className="cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => navigate(`/app/aplicativos/${ext.slug}`)}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <Icon className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{ext.title}</CardTitle>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={(e) => toggleFavorite(ext.id, e)}
                  >
                    <Star
                      className={`h-4 w-4 ${
                        favoriteIds.has(ext.id)
                          ? "fill-yellow-400 text-yellow-400"
                          : "text-muted-foreground"
                      }`}
                    />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription className="mb-4">
                  {ext.short_description}
                </CardDescription>

                <div className="flex flex-wrap gap-2">
                  {ext.pricing_type === "free" ? (
                    <Badge variant="secondary">Gratuito</Badge>
                  ) : (
                    <Badge variant="default">
                      {formatPrice(ext.price_cents)}
                    </Badge>
                  )}

                  {ext.health_areas.map((area, idx) => (
                    <Badge key={idx} variant="outline">
                      {area.name}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
